import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Search, Trash2 } from 'lucide-react';
import { trainingService } from '../../services/training-service';
import type { TrainingAdminDto, TrainingAdminQueryDto, CancelTrainingDto } from '../../types/admin-api';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

const columnHelper = createColumnHelper<TrainingAdminDto>();

export function TrainingList() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    trainingId: string;
    trainingTitle: string;
  }>({
    isOpen: false,
    trainingId: '',
    trainingTitle: '',
  });
  const [cancelReason, setCancelReason] = useState('');

  const query: TrainingAdminQueryDto = {
    page,
    pageSize,
    search: search || undefined,
    status: status === 'all' ? undefined : (status as 'active' | 'disabled'),
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['trainings', query],
    queryFn: () => trainingService.getAll(query),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => {
      const cancelData: CancelTrainingDto = { reason };
      return trainingService.cancel(id, cancelData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setConfirmDialog({ isOpen: false, trainingId: '', trainingTitle: '' });
      setCancelReason('');
    },
  });

  const handleCancelTraining = (training: TrainingAdminDto) => {
    setConfirmDialog({
      isOpen: true,
      trainingId: training.id,
      trainingTitle: training.title,
    });
  };

  const handleConfirmCancel = () => {
    cancelMutation.mutate({
      id: confirmDialog.trainingId,
      reason: cancelReason,
    });
  };

  const columns = [
    columnHelper.accessor('title', {
      header: 'Training Title',
      cell: (info) => (
        <div>
          <div className="font-medium text-ink-primary">{info.getValue()}</div>
          {info.row.original.specialization && (
            <div className="text-sm text-ink-muted">{info.row.original.specialization}</div>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('trainerName', {
      header: 'Trainer',
      cell: (info) => <span className="text-ink-secondary">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('facilityName', {
      header: 'Facility',
      cell: (info) => <span className="text-ink-secondary">{info.getValue() || '-'}</span>,
    }),
    columnHelper.accessor('duration', {
      header: 'Duration',
      cell: (info) => <span className="text-ink-secondary">{info.getValue()} min</span>,
    }),
    columnHelper.accessor('maxParticipants', {
      header: 'Max Participants',
      cell: (info) => <span className="text-ink-secondary">{info.getValue()}</span>,
    }),
    columnHelper.accessor('price', {
      header: 'Price',
      cell: (info) => (
        <span className="text-ink-secondary">
          PLN {info.getValue().toLocaleString('pl-PL', { minimumFractionDigits: 2 })}
        </span>
      ),
    }),
    columnHelper.accessor('sessionCount', {
      header: 'Sessions',
      cell: (info) => <span className="text-ink-secondary">{info.getValue()}</span>,
    }),
    columnHelper.accessor('participantCount', {
      header: 'Participants',
      cell: (info) => <span className="text-ink-secondary">{info.getValue()}</span>,
    }),
    columnHelper.accessor('isEnabled', {
      header: 'Status',
      cell: (info) => <StatusBadge isEnabled={info.getValue()} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center gap-2">
          {info.row.original.isEnabled && (
            <button
              onClick={() => handleCancelTraining(info.row.original)}
              className="p-1 text-brand-warm hover:bg-brand-warm/10 rounded"
              title="Cancel Training"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    return (
      <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded">
        Error loading trainings: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Filters */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm p-6">
        <h2 className="font-display text-xl font-semibold tracking-tight text-ink-primary mb-4">All Trainings</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-subtle"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by title..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="cancelled">Cancelled Only</option>
          </select>

          {/* Clear Filters */}
          {(search || status !== 'all') && (
            <button
              onClick={() => {
                setSearch('');
                setStatus('all');
                setPage(1);
              }}
              className="px-4 py-2 text-white bg-white/10 border border-white/30 rounded-lg hover:bg-white/20"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-app-elevated border border-white/10 rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-ink-muted">Loading trainings...</div>
        ) : data?.data && data.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-6 py-3 text-left text-xs font-medium text-ink-secondary uppercase tracking-wider"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-white/10">
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-white/5">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination && data.pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between">
                <div className="text-sm text-ink-secondary">
                  Showing page {data.pagination.page} of {data.pagination.totalPages} (
                  {data.pagination.totalItems} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="px-4 py-2 bg-white/10 border border-white/30 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center text-ink-muted">No trainings found</div>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => {
          setConfirmDialog({ isOpen: false, trainingId: '', trainingTitle: '' });
          setCancelReason('');
        }}
        onConfirm={handleConfirmCancel}
        title="Cancel Training"
        message={`Are you sure you want to cancel "${confirmDialog.trainingTitle}"? This action cannot be undone.`}
        confirmText="Cancel Training"
        confirmVariant="danger"
        isLoading={cancelMutation.isPending}
      >
        <div className="mt-4">
          <label htmlFor="cancelReason" className="block text-sm font-medium text-white/70 mb-2">
            Cancellation Reason (required)
          </label>
          <textarea
            id="cancelReason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            className="w-full px-3 py-2 bg-surface-input border border-white/15 text-ink-primary placeholder-ink-muted rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            rows={3}
            placeholder="Enter reason for cancellation..."
            required
          />
        </div>
      </ConfirmDialog>
    </div>
  );
}
